import { ChangeDetectionStrategy, Component, Inject, OnDestroy, ViewChild } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { MatSlideToggle, MatSlideToggleChange } from '@angular/material/slide-toggle';
import { MatIcon } from '@angular/material/icon';
import { SelectedService, selectedServiceToken } from 'go-modules/services/selected/selected.service';
import { Activity } from '@go-modules/interfaces/activity';
import { ConfirmDialogComponent, ConfirmDialogData } from '../dialogs/confirm-dialog/confirm-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { finalize, take } from 'rxjs';
import { NgxActivityService } from '@go-modules/services/activity/activity.service';
import { NgxTaskService } from '@go-modules/services/task/task.service';
import { ROUTE_NAMES, RouterService } from '@go-modules/services/router/router.service';
import { MatTooltip } from '@angular/material/tooltip';

@Component({
	selector: 'activity-task-toggle',
	templateUrl: './activity-task-toggle.component.html',
	styleUrls: ['./activity-task-toggle.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		CommonModule,
		TranslateModule,
		MatSlideToggle,
		MatIcon,
		MatTooltip
	]
})
export class ActivityTaskToggleComponent implements OnDestroy {
	@ViewChild(MatSlideToggle)
	private activityTaskToggle?: MatSlideToggle;
	public isTogglingActivityTasks = false;

	constructor (
		@Inject(selectedServiceToken) public selectedService: SelectedService,
		private dialog: MatDialog,
		private translate: TranslateService,
		private ngxActivityService: NgxActivityService,
		private ngxTaskService: NgxTaskService,
		private routerService: RouterService
	) {}

	public ngOnDestroy (): void {
		this.ngxActivityService.showBuildingActivityZeroState$.next(false);
	}

	public onActivityTaskToggleChange (change: MatSlideToggleChange): void {
		const {checked} = change;
		const activity = this.selectedService.getActivity() as Activity;
		if (!activity) {
			return;
		}

		const previousValue = activity.show_tasks;

		// Skip when the value is unchanged to avoid extra dialogs or service calls.
		if (previousValue === checked) {
			if (this.activityTaskToggle) {
				this.activityTaskToggle.checked = checked;
			}
			return;
		}

		if (checked === false) {
			this.dialog.open<ConfirmDialogComponent, ConfirmDialogData, boolean>(ConfirmDialogComponent, {
				data: {
					title: this.translate.instant('activity-task-toggle-disable-title'),
					message: this.translate.instant('activity-task-toggle-disable-message'),
					confirmText: this.translate.instant('common_continue'),
					cancelText: this.translate.instant('common_cancel')
				}
			})
				.afterClosed()
				.pipe(take(1))
				.subscribe((confirmed) => {
					if (confirmed) {
						this.toggleActivityTasks(activity, checked);
						return;
					}

					activity.show_tasks = previousValue;
					this.selectedService.setActivity(activity);
					if (this.activityTaskToggle) {
						this.activityTaskToggle.checked = previousValue;
					}
				});
			return;
		}

		this.toggleActivityTasks(activity, checked);
	}

	private toggleActivityTasks (activity: Activity, checked: boolean): void {
		this.isTogglingActivityTasks = true;
		const previousValue = activity.show_tasks;
		if (this.activityTaskToggle) {
			this.activityTaskToggle.checked = checked;
		}

		if (checked) {
			this.ngxActivityService.showBuildingActivityZeroState$.next(true);
		}
		this.ngxActivityService.toggleShowTasks(activity.activity_id, checked)
			.pipe(
				finalize(() => {
					this.isTogglingActivityTasks = false;
					this.ngxActivityService.showBuildingActivityZeroState$.next(false);
				})
			)
			.subscribe({
				next: (updatedActivity) => {
					if (updatedActivity && Object.prototype.hasOwnProperty.call(updatedActivity, 'show_tasks')) {
						activity.show_tasks = updatedActivity.show_tasks === true;
					} else {
						activity.show_tasks = checked;
					}
					this.selectedService.setActivity(activity);
					this.ngxTaskService.clearCache(activity.activity_id);
					if (checked) {
						this.ngxTaskService.getDefaultTaskTypeForUser().subscribe((taskType) => {
							this.routerService.navigateTo(ROUTE_NAMES.DASHBOARD_ACTIVITY_TASK, {
								activity_id: activity.activity_id,
								type: taskType
							});
						});
					} else {
						this.routerService.navigateTo(ROUTE_NAMES.DASHBOARD_ACTIVITY_VIEW, {
							activity_id: activity.activity_id
						});
					}
				},
				error: () => {
					activity.show_tasks = previousValue;
					this.selectedService.setActivity(activity);
					if (this.activityTaskToggle) {
						this.activityTaskToggle.checked = previousValue;
					}
				}
			});
	}
}
